import { useSelector } from 'react-redux'

import { orgSelectors } from 'ducks/orgs'
import { HardwareSupplierFilterKeyType } from 'pages/ordering/type'
import { useMemo } from 'react'
import { DistributorDataTypeV2 } from './types'

const useTotalStockAcrossSuppliers = (
  distributors: DistributorDataTypeV2[],
  source?: HardwareSupplierFilterKeyType | 'component' | null | undefined
): { availableQuantity: number; isGlobalOutOfStock: boolean; showIsInStockElsewhere: boolean } => {
  const enabledDistributorsFilterKeys = useSelector(orgSelectors.getEnabledHardwareDistributorFilterKeys)
  const enabledDistributorsConfig = useSelector(orgSelectors.getEnabledSellerIntegrationsConfig)

  const filteredDistributors = useMemo(() => {
    return distributors.filter(
      (item) =>
        item.is_available &&
        enabledDistributorsFilterKeys.includes(item.distributor) &&
        // When we have a source, and it's not the manual cost, we only count stocks for the source supplier
        (source && source !== 'component' ? item.distributor === source : true)
    )
  }, [distributors, enabledDistributorsFilterKeys, source])

  const isGlobalOutOfStock = useMemo(
    () =>
      filteredDistributors.every((distributorData) => {
        const globalStockLevel = distributorData.stock_levels?.find(({ location }) => location === null)
        if (!globalStockLevel) {
          if (distributorData.is_in_stock === true) {
            return false
          }
          if (distributorData.is_in_stock === false) {
            return true
          }
          return true
        }
        if (!globalStockLevel.in_stock) {
          return true
        }
        return globalStockLevel.quantity === 0
      }),
    [filteredDistributors]
  )

  const availableQuantity = useMemo(() => {
    return filteredDistributors.reduce((currentQuantity, distributorData) => {
      const preferredStockLocation =
        enabledDistributorsConfig.find((distributor) => distributor.filterKey === distributorData.distributor)
          ?.preferredStockLocation ?? null
      const stockLevel = distributorData.stock_levels?.find(
        ({ location }) => (location?.key ?? null) === preferredStockLocation && (location?.is_active ?? true)
      )

      if (!stockLevel) {
        const globalStockLevel = distributorData.stock_levels?.find(({ location }) => location === null)
        if (!globalStockLevel) {
          return currentQuantity
        }
        if (!globalStockLevel.in_stock) {
          return currentQuantity
        }
        return currentQuantity + globalStockLevel.quantity
      }

      return currentQuantity + stockLevel.quantity
    }, 0)
  }, [filteredDistributors, enabledDistributorsConfig])

  const showIsInStockElsewhere = useMemo(() => {
    if (!source || source === 'component') {
      return false
    }
    return (
      (availableQuantity === 0 &&
        !isGlobalOutOfStock &&
        filteredDistributors.some((distributor) =>
          distributor.stock_levels?.some(
            (stock) => stock.in_stock && stock.quantity > 0 && (stock.location?.is_active ?? false)
          )
        )) ??
      false
    )
  }, [availableQuantity, isGlobalOutOfStock, filteredDistributors, source])

  return {
    availableQuantity,
    isGlobalOutOfStock,
    showIsInStockElsewhere,
  }
}

export default useTotalStockAcrossSuppliers
